import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  BadgeDollarSign,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  Clock,
  Layers3,
  RadioTower,
  Search,
} from "lucide-react";
import API from "../services/api";
import CustomSelect from "../components/CustomSelect";
import IpAuditModal from "../components/IpAuditModal";
import PageModal from "../components/PageModal";
import PremiumPairValue from "../components/PremiumPairValue";
import RadioButtonGroup from "../components/RadioButtonGroup";
import { useToast } from "../components/Toast";
import { getAccountSets } from "../services/sessionCache";
import { resolveAccountSetId, setStoredAccountSetId } from "../services/accountSetSelection";
import {
  connectWS,
  onMessage,
  subscribeToPremium,
  getProfitStreamHealth,
} from "../services/wsService";
import { formatAppDateTime } from "../utils/dateFormat";
import { playOrderExecuted, playOrderCancelled } from "../services/sound";
import { hasNonZeroPremiumPair } from "../utils/premiumDisplay";
import { normalizeTakeProfitSubmission } from "../utils/takeProfitSubmission";
import "./admin/AdminShared.css";
import "./admin/AdminBroadcast.css";
import "./admin/AdminAccounts.css";
import "./admin/AdminUsers.css";
import "./PendingTrades.css";

const pageSizeOptions = [
  { value: "10", label: "10" },
  { value: "50", label: "50" },
  { value: "100", label: "100" },
];

const statusOptions = [
  { value: "", label: "All Status" },
  { value: "Pending", label: "Pending" },
  { value: "Executing", label: "Executing" },
];

const PENDING_ORDERS_FALLBACK_REFRESH_MS = 15000;
const LADDER_TERMINAL_STEP_STATUSES = new Set(["EXECUTED", "COMPLETED_TP", "SKIPPED"]);
const LADDER_EXECUTING_MESSAGE =
  "Ladder has an order executing. Wait for it to finish before reordering.";
const LADDER_PAUSED_MESSAGE =
  "Ladder has a paused step. Resume, skip, or reset it before reordering.";

function getIpAuditEntries(record, { includeManualClose = false } = {}) {
  const entries = [];

  if (record?.createIp) entries.push({ label: "Created", value: String(record.createIp) });
  if (record?.updateIp) entries.push({ label: "Updated", value: String(record.updateIp) });
  if (includeManualClose && record?.manualCloseIp) {
    entries.push({ label: "Closed", value: String(record.manualCloseIp) });
  }

  return entries;
}

export default function PendingTrades() {
  const toast = useToast();
  const [accountSets, setAccountSets] = useState([]);
  const [selectedSetId, setSelectedSetId] = useState("");
  const [pendingOrders, setPendingOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [plHealth, setPlHealth] = useState({ healthy: false, resubRecent: false });
  const [buyPremium, setBuyPremium] = useState(0);
  const [sellPremium, setSellPremium] = useState(0);
  const [premiumLive, setPremiumLive] = useState(false);
  const [collectorStale, setCollectorStale] = useState(false);

  const [editingOrder, setEditingOrder] = useState(null);
  const [editTargetPremium, setEditTargetPremium] = useState("");
  const [editTakeProfit, setEditTakeProfit] = useState("");
  const [editTakeProfitMode, setEditTakeProfitMode] = useState("None");
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState("");
  const [savingScalping, setSavingScalping] = useState(new Set());
  const pendingScalpingOverridesRef = useRef({});

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sortKey, setSortKey] = useState("createdAt");
  const [sortDirection, setSortDirection] = useState("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [ladderReorderBusy, setLadderReorderBusy] = useState("");
  const recentExecutedSoundRef = useRef(new Set());
  const recentCancelledSoundRef = useRef(new Set());
  const [selectedAuditIp, setSelectedAuditIp] = useState("");

  const getResponseMessage = (payload, fallback) => payload?.message || payload?.error || fallback;

  const getErrorMessage = (err, fallback) =>
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.response?.data?.msg ||
    err?.message ||
    fallback;

  const roundPendingDraftNumber = (value) => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return null;
    return Math.round(numericValue * 100) / 100;
  };
  const formatPendingDraftValue = (value) => {
    if (value === null || value === undefined || value === "") return "";
    const roundedValue = roundPendingDraftNumber(value);
    if (!Number.isFinite(roundedValue)) return "";
    return String(roundedValue);
  };
  const formatPendingTpValue = (value) => {
    const roundedValue = roundPendingDraftNumber(value);
    if (!Number.isFinite(roundedValue)) return value;
    return roundedValue.toFixed(2);
  };
  const handlePendingNumericDraftChange = (setter, rawValue) => {
    if (rawValue === "") {
      setter("");
      return;
    }

    if (!/^-?\d*\.?\d*$/.test(rawValue)) {
      return;
    }

    setter(rawValue);
  };

  const getPendingOrderKey = (orderOrId) =>
    String(orderOrId?.orderId || orderOrId?.id || orderOrId || "");

  const applyPendingScalpingOverride = useCallback((order) => {
    const key = getPendingOrderKey(order);
    if (!key) return order;
    if (!Object.prototype.hasOwnProperty.call(pendingScalpingOverridesRef.current, key)) {
      return order;
    }
    return {
      ...order,
      scalpingMode: Boolean(pendingScalpingOverridesRef.current[key]),
    };
  }, []);

  const markRecentSound = useCallback((ref, key) => {
    if (!key) return false;
    if (ref.current.has(key)) return true;
    ref.current.add(key);
    window.setTimeout(() => {
      ref.current.delete(key);
    }, 5000);
    return false;
  }, []);

  const loadAccountSets = useCallback(async () => {
    try {
      const data = await getAccountSets();
      if (Array.isArray(data)) {
        setAccountSets(data);
        if (data.length > 0) {
          setSelectedSetId((currentSelectedId) => {
            const selectedId = resolveAccountSetId(data, currentSelectedId);
            if (selectedId) setStoredAccountSetId(selectedId);
            return selectedId;
          });
        } else {
          setSelectedSetId("");
          setPendingOrders([]);
          setLoading(false);
        }
      } else {
        setAccountSets([]);
        setSelectedSetId("");
        setPendingOrders([]);
        setLoading(false);
      }
    } catch (err) {
      console.error("Failed to load account sets:", err);
      setLoading(false);
    }
  }, []);

  const loadPendingOrders = useCallback(
    async (showLoading = true) => {
      if (!selectedSetId) {
        setPendingOrders([]);
        if (showLoading) setLoading(false);
        return;
      }
      try {
        if (showLoading) setLoading(true);
        const res = await API.get(`/trading/pending-orders?accountSetId=${selectedSetId}`);
        if (res.data.success) {
          const nextOrders = Array.isArray(res.data.orders)
            ? res.data.orders.map(applyPendingScalpingOverride)
            : [];
          setPendingOrders(nextOrders);
        }
      } catch (err) {
        console.error("Failed to load pending orders:", err);
      } finally {
        if (showLoading) setLoading(false);
      }
    },
    [selectedSetId, applyPendingScalpingOverride],
  );

  useEffect(() => {
    const id = setInterval(() => setPlHealth(getProfitStreamHealth()), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    loadAccountSets();
  }, [loadAccountSets]);

  useEffect(() => {
    setPremiumLive(false);
    setCollectorStale(false);
  }, [selectedSetId]);

  useEffect(() => {
    if (!selectedSetId) return;
    const selectedAccountSet = accountSets.find((s) => (s._id || s.id) === selectedSetId);
    if (!selectedAccountSet) return;

    connectWS(selectedAccountSet._id || selectedAccountSet.id);

    if (
      selectedAccountSet.symbolsLocked &&
      selectedAccountSet.futureSymbol &&
      selectedAccountSet.spotSymbol
    ) {
      subscribeToPremium(
        selectedAccountSet._id || selectedAccountSet.id,
        selectedAccountSet.futureSymbol,
        selectedAccountSet.spotSymbol,
      );
    }

    const handlePremiumUpdate = (data) => {
      if (data.accountSetId === (selectedAccountSet._id || selectedAccountSet.id)) {
        setBuyPremium(data.buyPremium || 0);
        setSellPremium(data.sellPremium || 0);
        setPremiumLive(Boolean(data.premiumLive));
        setCollectorStale(Boolean(data.collectorStale));
      }
    };

    const unsubscribe = onMessage("premium_update", handlePremiumUpdate);
    return () => {
      unsubscribe();
    };
  }, [selectedSetId, accountSets]);

  useEffect(() => {
    if (!selectedSetId) return;

    const refreshPendingOrders = () => {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") {
        return;
      }
      loadPendingOrders(false);
    };

    const handleVisibilityChange = () => {
      if (typeof document !== "undefined" && document.visibilityState === "visible") {
        refreshPendingOrders();
      }
    };

    const handlePendingUpdate = (msg) => {
      const payload = msg?.data ?? msg;
      if (!payload || String(payload.accountSetId) !== String(selectedSetId)) return;
      const updated = payload;
      setPendingOrders((prev) => {
        const updatedId = String(updated.orderId || updated.id || "");
        const index = prev.findIndex((o) => String(o.orderId || o.id || "") === updatedId);
        if (["Pending", "Executing"].includes(updated.status)) {
          const prevOrder = index >= 0 ? prev[index] : undefined;
          const anyWithBrokers = prev.find((o) => o.broker1 && o.broker2);
          const merged = applyPendingScalpingOverride({
            ...updated,
            broker1: updated.broker1 ?? prevOrder?.broker1 ?? anyWithBrokers?.broker1,
            broker2: updated.broker2 ?? prevOrder?.broker2 ?? anyWithBrokers?.broker2,
          });
          if (index >= 0) {
            const next = [...prev];
            next[index] = merged;
            return next;
          }
          return [...prev, merged];
        }
        if (index >= 0) {
          const soundKey = updatedId || String(updated.orderId || updated.id || "");
          if (["Executed", "Triggered", "Filled"].includes(updated.status)) {
            if (!markRecentSound(recentExecutedSoundRef, soundKey)) {
              try {
                playOrderExecuted();
              } catch {}
            }
          } else if (["Cancelled", "Canceled"].includes(updated.status)) {
            if (!markRecentSound(recentCancelledSoundRef, soundKey)) {
              try {
                playOrderCancelled();
              } catch {}
            }
          }
          if (soundKey) {
            delete pendingScalpingOverridesRef.current[soundKey];
          }
          const next = [...prev];
          next.splice(index, 1);
          return next;
        }
        return prev;
      });
    };

    const handlePendingResync = (msg) => {
      const payload = msg?.data ?? msg;
      const incomingAccountSetId = msg?.accountSetId ?? payload?.accountSetId;
      if (incomingAccountSetId && String(incomingAccountSetId) !== String(selectedSetId)) return;
      refreshPendingOrders();
    };

    const off = onMessage("pending_order_update", handlePendingUpdate);
    const offConnection = onMessage("connection", handlePendingResync);
    const offSubscription = onMessage("subscription_confirmed", handlePendingResync);
    const interval = setInterval(refreshPendingOrders, PENDING_ORDERS_FALLBACK_REFRESH_MS);
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", handleVisibilityChange);
    }
    return () => {
      off && off();
      offConnection && offConnection();
      offSubscription && offSubscription();
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", handleVisibilityChange);
      }
      clearInterval(interval);
    };
  }, [selectedSetId, loadPendingOrders, markRecentSound, applyPendingScalpingOverride]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, sortKey, sortDirection, pageSize, selectedSetId]);

  useEffect(() => {
    if (selectedSetId) {
      loadPendingOrders();
    }
  }, [selectedSetId, loadPendingOrders, applyPendingScalpingOverride]);

  const cancelPendingOrder = async (orderId) => {
    try {
      const res = await API.post("/trading/cancel-pending", { orderId });
      if (res.data.success) {
        if (!markRecentSound(recentCancelledSoundRef, String(orderId || ""))) {
          try {
            playOrderCancelled();
          } catch {}
        }
        await loadPendingOrders(false);
        toast.success(getResponseMessage(res.data, "Pending order cancelled successfully"));
        return;
      }
      toast.error(getResponseMessage(res.data, "Failed to cancel pending order"));
    } catch (err) {
      console.error("Failed to cancel pending order:", err);
      toast.error(getErrorMessage(err, "Failed to cancel pending order"));
    }
  };

  const openEditPendingOrder = (order) => {
    const currentTarget = order?.targetPremium ?? "";
    const currentTp = order?.takeProfit ?? "";
    const currentMode =
      order?.takeProfitMode && order.takeProfitMode !== "None"
        ? order.takeProfitMode
        : order?.pendingMode === "AI Smart Ladder"
          ? "Premium"
          : "None";

    setEditingOrder(order);
    setEditTargetPremium(
      currentTarget === null || currentTarget === undefined || currentTarget === ""
        ? ""
        : formatPendingDraftValue(currentTarget),
    );
    setEditTakeProfit(
      currentTp === null || currentTp === undefined || currentTp === ""
        ? ""
        : formatPendingDraftValue(currentTp),
    );
    setEditTakeProfitMode(currentMode || "None");
    setEditError("");
  };

  const closeEditPendingOrder = () => {
    setEditingOrder(null);
    setEditError("");
  };

  const submitEditPendingOrder = async () => {
    try {
      if (!editingOrder) return;
      if (!editTargetPremium) {
        setEditError("Target premium is required.");
        return;
      }
      setEditSaving(true);
      const { takeProfit: normalizedTakeProfit, takeProfitMode: normalizedTakeProfitMode } =
        normalizeTakeProfitSubmission(editTakeProfit, editTakeProfitMode);
      const res = await API.post("/trading/update-pending", {
        orderId: editingOrder.orderId || editingOrder.id,
        targetPremium: editTargetPremium,
        takeProfit: normalizedTakeProfit,
        takeProfitMode: normalizedTakeProfitMode,
      });
      if (res.data.success) {
        await loadPendingOrders(false);
        toast.success(getResponseMessage(res.data, "Pending order updated successfully"));
        closeEditPendingOrder();
        return;
      }
      const message = getResponseMessage(res.data, "Failed to update order. Please try again.");
      setEditError(message);
      toast.error(message);
    } catch (err) {
      const message = getErrorMessage(err, "Failed to update order. Please try again.");
      setEditError(message);
      toast.error(message);
      console.error("Failed to update pending order:", err);
    } finally {
      setEditSaving(false);
    }
  };

  const updatePendingScalpingMode = async (order, nextScalpingMode) => {
    const orderId = order.orderId || order.id;
    const key = getPendingOrderKey(orderId);
    if (!key) return;

    const previousScalpingMode = Boolean(order.scalpingMode);
    pendingScalpingOverridesRef.current[key] = nextScalpingMode;
    setPendingOrders((prev) =>
      prev.map((item) =>
        getPendingOrderKey(item) === key ? { ...item, scalpingMode: nextScalpingMode } : item,
      ),
    );
    setSavingScalping((prev) => new Set(prev).add(key));

    try {
      const res = await API.post("/trading/update-pending", {
        orderId,
        scalpingMode: nextScalpingMode,
      });

      if (!res.data?.success) {
        throw new Error(res.data?.message || "Failed to update scalping mode");
      }

      const serverScalpingMode = res.data?.pendingOrder?.scalpingMode;
      if (typeof serverScalpingMode === "boolean") {
        pendingScalpingOverridesRef.current[key] = serverScalpingMode;
        setPendingOrders((prev) =>
          prev.map((item) =>
            getPendingOrderKey(item) === key ? { ...item, scalpingMode: serverScalpingMode } : item,
          ),
        );
      }
    } catch (err) {
      pendingScalpingOverridesRef.current[key] = previousScalpingMode;
      setPendingOrders((prev) =>
        prev.map((item) =>
          getPendingOrderKey(item) === key ? { ...item, scalpingMode: previousScalpingMode } : item,
        ),
      );
      const msg = getErrorMessage(err, "Unknown error");
      toast.error(`Failed to update scalping mode: ${msg}`);
    } finally {
      setSavingScalping((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  const handleLadderAction = async (orderId, action) => {
    try {
      const res = await API.post("/trading/ladder-action", { orderId, action });
      if (res.data.success) {
        await loadPendingOrders(false);
        toast.success(getResponseMessage(res.data, `Ladder action ${action} applied`));
        return;
      }
      toast.error(getResponseMessage(res.data, `Failed to ${action} ladder step`));
    } catch (err) {
      console.error("Failed to apply ladder action:", err);
      toast.error(getErrorMessage(err, `Failed to ${action} ladder step`));
    }
  };

  const formatDate = (dateString) => formatAppDateTime(dateString);
  const getPendingTpDisplayValue = (order) => {
    if (order.takeProfitMode === "None" || !order.takeProfit) return "Not Set";
    if (order.takeProfitMode === "Premium") return formatPendingTpValue(order.takeProfit);
    if (order.takeProfitMode === "Amount") return `$${formatPendingTpValue(order.takeProfit)}`;
    return String(order.takeProfit);
  };
  const getPendingTpModeLabel = (order) => {
    if (order.takeProfitMode === "Premium") return "Deficit Premium";
    if (order.takeProfitMode === "Amount") return "Amount";
    return "No Target";
  };
  const getPendingTpStatus = (order) => {
    if (order.takeProfitMode === "None" || !order.takeProfit) {
      return { value: "", suffix: "No TP configured", highlight: false, visible: true };
    }
    return { value: "", suffix: "Starts after entry", highlight: false, visible: true };
  };
  const maskLadderGroupId = (value) => {
    const text = String(value || "").trim();
    if (!text) return "—";
    if (text.length <= 8) return text;
    return `${text.slice(0, 4)}***${text.slice(-4)}`;
  };

  const isActivePendingLadderStep = useCallback(
    (order) =>
      order?.pendingMode === "AI Smart Ladder" &&
      order?.ladderGroupId &&
      ["pending", "executing"].includes(String(order?.status || "").toLowerCase()) &&
      !LADDER_TERMINAL_STEP_STATUSES.has(String(order?.ladderStepStatus || "").toUpperCase()),
    [],
  );

  const visibleOrders = useMemo(
    () =>
      pendingOrders.filter((order) => {
        if (order?.pendingMode !== "AI Smart Ladder") return true;
        return !LADDER_TERMINAL_STEP_STATUSES.has(
          String(order.ladderStepStatus || "").toUpperCase(),
        );
      }),
    [pendingOrders],
  );

  const ladderGroupsById = useMemo(() => {
    const groups = new Map();

    visibleOrders.forEach((order) => {
      if (!isActivePendingLadderStep(order)) return;
      const groupId = String(order.ladderGroupId);
      if (!groups.has(groupId)) groups.set(groupId, []);
      groups.get(groupId).push(order);
    });

    groups.forEach((rows) => {
      rows.sort((a, b) => {
        const aStep = Number.isFinite(Number(a.ladderStepIndex))
          ? Number(a.ladderStepIndex)
          : Number.MAX_SAFE_INTEGER;
        const bStep = Number.isFinite(Number(b.ladderStepIndex))
          ? Number(b.ladderStepIndex)
          : Number.MAX_SAFE_INTEGER;
        if (aStep !== bStep) return aStep - bStep;

        const aCreated = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const bCreated = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        if (aCreated !== bCreated) return aCreated - bCreated;

        return getPendingOrderKey(a).localeCompare(getPendingOrderKey(b));
      });
    });

    return groups;
  }, [visibleOrders, isActivePendingLadderStep]);

  const mergePendingOrderUpdates = useCallback(
    (incomingOrders = []) => {
      const normalizedIncoming = incomingOrders.map(applyPendingScalpingOverride);

      setPendingOrders((currentOrders) => {
        const byId = new Map(normalizedIncoming.map((order) => [getPendingOrderKey(order), order]));
        const nextOrders = currentOrders.map((order) => {
          const key = getPendingOrderKey(order);
          return byId.get(key) || order;
        });
        const existingIds = new Set(nextOrders.map((order) => getPendingOrderKey(order)));

        normalizedIncoming.forEach((order) => {
          const key = getPendingOrderKey(order);
          if (key && !existingIds.has(key)) {
            nextOrders.push(order);
          }
        });

        return nextOrders;
      });
    },
    [applyPendingScalpingOverride],
  );

  const reorderLadderGroup = useCallback(
    async (order, direction) => {
      if (!isActivePendingLadderStep(order)) return;

      const ladderGroupId = String(order.ladderGroupId || "");
      const groupRows = ladderGroupsById.get(ladderGroupId) || [];
      if (groupRows.length <= 1) return;

      if (groupRows.some((row) => String(row.status || "").toLowerCase() === "executing")) {
        toast.error(LADDER_EXECUTING_MESSAGE);
        return;
      }

      if (groupRows.some((row) => String(row.ladderStepStatus || "").toUpperCase() === "PAUSED")) {
        toast.error(LADDER_PAUSED_MESSAGE);
        return;
      }

      const currentOrderKey = getPendingOrderKey(order);
      const currentIndex = groupRows.findIndex(
        (row) => getPendingOrderKey(row) === currentOrderKey,
      );
      const nextIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;
      if (currentIndex < 0 || nextIndex < 0 || nextIndex >= groupRows.length) return;

      const reorderedRows = [...groupRows];
      [reorderedRows[currentIndex], reorderedRows[nextIndex]] = [
        reorderedRows[nextIndex],
        reorderedRows[currentIndex],
      ];

      const orderIds = reorderedRows.map((row) => getPendingOrderKey(row)).filter(Boolean);
      if (orderIds.length !== groupRows.length || new Set(orderIds).size !== orderIds.length) {
        toast.error("Unable to reorder ladder steps because order IDs are incomplete.");
        return;
      }

      const accountSetId =
        order.accountSetId || order.accountSet?._id || order.accountSet?.id || selectedSetId;

      setLadderReorderBusy(ladderGroupId);
      try {
        const res = await API.post("/trading/ladder-reorder", {
          ladderGroupId,
          accountSetId,
          orderIds,
        });
        const payload = res.data || {};

        if (payload.success) {
          if (Array.isArray(payload.pendingOrders) && payload.pendingOrders.length > 0) {
            mergePendingOrderUpdates(payload.pendingOrders);
          } else {
            await loadPendingOrders(false);
          }

          toast.success(getResponseMessage(payload, "Ladder steps reordered successfully"));
          if (payload.hasActiveTrade) {
            toast.info(
              "Active ladder trade is still open. Reordered steps will apply to the remaining sequence after the current trade closes.",
            );
          }
        } else {
          toast.error(getResponseMessage(payload, "Failed to reorder ladder steps"));
          await loadPendingOrders(false);
        }
      } catch (err) {
        toast.error(getErrorMessage(err, "Failed to reorder ladder steps"));
        await loadPendingOrders(false);
      } finally {
        setLadderReorderBusy("");
      }
    },
    [
      isActivePendingLadderStep,
      ladderGroupsById,
      selectedSetId,
      toast,
      mergePendingOrderUpdates,
      loadPendingOrders,
    ],
  );

  const processedOrders = useMemo(() => {
    let result = [...visibleOrders];

    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      result = result.filter(
        (o) =>
          String(o.orderId || "")
            .toLowerCase()
            .includes(q) ||
          String(o.direction || "")
            .toLowerCase()
            .includes(q) ||
          String(o.pendingMode || "")
            .toLowerCase()
            .includes(q) ||
          String(o.status || "")
            .toLowerCase()
            .includes(q),
      );
    }

    if (statusFilter) {
      result = result.filter((o) => o.status === statusFilter);
    }

    result.sort((a, b) => {
      let aVal, bVal;
      if (sortKey === "orderId") {
        aVal = String(a.orderId || "");
        bVal = String(b.orderId || "");
        return sortDirection === "asc" ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      if (sortKey === "direction") {
        aVal = a.direction || "";
        bVal = b.direction || "";
        return sortDirection === "asc" ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      if (sortKey === "volume") {
        aVal = parseFloat(a.volume) || 0;
        bVal = parseFloat(b.volume) || 0;
      } else if (sortKey === "targetPremium") {
        aVal = parseFloat(a.targetPremium) || 0;
        bVal = parseFloat(b.targetPremium) || 0;
      } else if (sortKey === "takeProfit") {
        aVal = parseFloat(a.takeProfit) || 0;
        bVal = parseFloat(b.takeProfit) || 0;
      } else if (sortKey === "pendingMode") {
        aVal = a.pendingMode || "";
        bVal = b.pendingMode || "";
        return sortDirection === "asc" ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      } else if (sortKey === "status") {
        aVal = a.status || "";
        bVal = b.status || "";
        return sortDirection === "asc" ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      } else {
        const aId = String(a.orderId || a.id || "");
        const bId = String(b.orderId || b.id || "");
        aVal = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        bVal = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        if (aVal !== bVal) return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
        return sortDirection === "asc" ? aId.localeCompare(bId) : bId.localeCompare(aId);
      }
      return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
    });

    return result;
  }, [visibleOrders, searchTerm, statusFilter, sortKey, sortDirection]);

  const accountSetOptions = useMemo(
    () => accountSets.map((s) => ({ value: s._id || s.id, label: s.name })),
    [accountSets],
  );

  const selectedAccountSet = accountSets.find((s) => (s._id || s.id) === selectedSetId);
  const pageCount = Math.max(1, Math.ceil(processedOrders.length / pageSize));
  const rowStart = processedOrders.length === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const rowEnd = Math.min(currentPage * pageSize, processedOrders.length);
  const paginatedOrders = processedOrders.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize,
  );

  const pendingCount = visibleOrders.filter((o) => o.status === "Pending").length;
  const executingCount = visibleOrders.filter((o) => o.status === "Executing").length;
  const hasPremiumData = hasNonZeroPremiumPair({ buyPremium, sellPremium }) || premiumLive;

  const plHealthClass = plHealth.healthy
    ? "admin-badge-success"
    : plHealth.resubRecent
      ? "admin-badge-warning"
      : "admin-badge-neutral";
  const plHealthLabel = plHealth.healthy
    ? "Healthy"
    : plHealth.resubRecent
      ? "Re-subscribed"
      : "Idle";

  const skeletonRows = useMemo(() => Array.from({ length: 6 }), []);

  const renderHeaderSort = (label, key) => (
    <button
      type="button"
      className={`pending-trades-sort${sortKey === key ? " pending-trades-sort-active" : ""}`}
      onClick={() => {
        if (sortKey === key) {
          setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
        } else {
          setSortKey(key);
          setSortDirection("desc");
        }
      }}
    >
      {label}
      {sortKey === key ? (
        <ChevronDown
          className={`pending-trades-sort-icon${sortDirection === "asc" ? "" : " pending-trades-sort-icon-desc"}`}
        />
      ) : (
        <ChevronsUpDown className="pending-trades-sort-icon" />
      )}
    </button>
  );

  return (
    <>
      <div className="admin-page admin-broadcast-page pending-trades-shell">
        <div className="admin-broadcast-header">
          <div className="admin-broadcast-header-copy">
            <h1>
              <Clock className="admin-broadcast-header-icon" />
              <span>Pending Trades</span>
            </h1>
            <p>Monitor pending orders waiting for target premium to be reached before execution.</p>
          </div>
        </div>

        <div className="admin-broadcast-summary-grid pending-trades-summary-grid">
          <div className="admin-broadcast-summary-card">
            <div className="admin-broadcast-summary-icon">
              <Layers3 size={20} />
            </div>
            <div className="admin-broadcast-summary-copy">
              <span>Account Set</span>
              <strong>{selectedAccountSet?.name || "No Set"}</strong>
              <small>
                {selectedAccountSet?.brokers?.[0]?.terminal &&
                selectedAccountSet?.brokers?.[1]?.terminal
                  ? `${selectedAccountSet.brokers[0].terminal} · ${selectedAccountSet.brokers[1].terminal}`
                  : "Select an account set"}
              </small>
            </div>
          </div>

          <div className="admin-broadcast-summary-card">
            <div className="admin-broadcast-summary-icon">
              <Clock size={20} />
            </div>
            <div className="admin-broadcast-summary-copy">
              <span>Pending Orders</span>
              <strong>{visibleOrders.length}</strong>
              <small>
                {pendingCount} Pending · {executingCount} Executing
              </small>
            </div>
          </div>

          <div className="admin-broadcast-summary-card">
            <div className="admin-broadcast-summary-icon">
              <RadioTower size={20} />
            </div>
            <div className="admin-broadcast-summary-copy">
              <span>
                {premiumLive
                  ? "Live Premium"
                  : collectorStale
                    ? "Last Known Premium"
                    : "Waiting for Premium"}
              </span>
              <strong>Sell {sellPremium.toFixed(2)}</strong>
              <small>Buy {buyPremium.toFixed(2)}</small>
            </div>
          </div>

          <div className="admin-broadcast-summary-card">
            <div className="admin-broadcast-summary-icon">
              <BadgeDollarSign size={20} />
            </div>
            <div className="admin-broadcast-summary-copy">
              <span>P&amp;L Health</span>
              <strong>
                <span className={`admin-badge pending-trades-health-badge ${plHealthClass}`}>
                  {plHealthLabel}
                </span>
              </strong>
              <small>Feed status</small>
            </div>
          </div>
        </div>

        <section className="admin-card admin-broadcast-card">
          <div className="admin-card-heading">
            <div className="admin-broadcast-card-copy">
              <div className="admin-card-title">
                <Clock />
                <span>Pending Orders Queue</span>
              </div>
              <p>
                Orders waiting for target premium. Ladder steps and scalping confirmations update in
                real time.
              </p>
            </div>
          </div>

          <div className="pending-trades-toolbar pending-trades-toolbar-primary">
            <div className="admin-field-group admin-users-toolbar-search">
              <label className="admin-label">
                <Search size={13} />
                Search
              </label>
              <input
                type="text"
                className="admin-input"
                placeholder="Search by order ID, direction, or mode"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="admin-field-group pending-trades-account-field">
              <label className="admin-label">Account Set</label>
              <CustomSelect
                value={selectedSetId}
                onChange={(value) => {
                  setSelectedSetId(value);
                  setStoredAccountSetId(value);
                }}
                options={accountSetOptions}
                placeholder="Select account set"
              />
            </div>
            <div className="admin-field-group pending-trades-status-field">
              <label className="admin-label">Status</label>
              <CustomSelect
                value={statusFilter}
                onChange={setStatusFilter}
                options={statusOptions}
              />
            </div>
            <div className="admin-field-group pending-trades-count-field">
              <label className="admin-label">Count</label>
              <CustomSelect
                value={String(pageSize)}
                onChange={(value) => setPageSize(Number(value))}
                options={pageSizeOptions}
              />
            </div>
          </div>

          {selectedAccountSet && (
            <div className="admin-meta pending-trades-account-meta">
              <span>
                Broker 1: {selectedAccountSet.brokers[0]?.terminal} (
                {selectedAccountSet.brokers[0]?.accountNumber}-
                {selectedAccountSet.brokers[0]?.brokerName})
              </span>
              <span>
                Broker 2: {selectedAccountSet.brokers[1]?.terminal} (
                {selectedAccountSet.brokers[1]?.accountNumber}-
                {selectedAccountSet.brokers[1]?.brokerName})
              </span>
            </div>
          )}

          <div className="admin-data-table-wrap admin-users-table-wrap pending-trades-table-wrap">
            <table className="admin-data-table admin-users-table pending-trades-table">
              <thead>
                <tr>
                  <th>S. No.</th>
                  <th>IP Audit</th>
                  <th>{renderHeaderSort("Order ID", "orderId")}</th>
                  <th>{renderHeaderSort("Direction", "direction")}</th>
                  <th>{renderHeaderSort("Volume", "volume")}</th>
                  <th>Broker 1</th>
                  <th>Broker 2</th>
                  <th>{renderHeaderSort("Target Premium", "targetPremium")}</th>
                  <th>Current Premium</th>
                  <th>{renderHeaderSort("Mode", "pendingMode")}</th>
                  <th>Scalp</th>
                  <th>Ladder Info</th>
                  <th>{renderHeaderSort("Current TP", "takeProfit")}</th>
                  <th>{renderHeaderSort("Status", "status")}</th>
                  <th>{renderHeaderSort("Created At", "createdAt")}</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  skeletonRows.map((_, i) => (
                    <tr key={`pt-skeleton-${i}`} className="admin-table-skeleton-row">
                      <td>
                        <span className="admin-skeleton-line admin-skeleton-line-sm" />
                      </td>
                      <td>
                        <span className="admin-skeleton-line admin-skeleton-line-lg" />
                      </td>
                      <td>
                        <span className="admin-skeleton-line admin-skeleton-line-md" />
                      </td>
                      <td>
                        <span className="admin-skeleton-line admin-skeleton-line-sm" />
                      </td>
                      <td>
                        <span className="admin-skeleton-line admin-skeleton-line-sm" />
                      </td>
                      <td>
                        <span className="admin-skeleton-line admin-skeleton-line-lg" />
                      </td>
                      <td>
                        <span className="admin-skeleton-line admin-skeleton-line-lg" />
                      </td>
                      <td>
                        <span className="admin-skeleton-line admin-skeleton-line-sm" />
                      </td>
                      <td>
                        <span className="admin-skeleton-line admin-skeleton-line-sm" />
                      </td>
                      <td>
                        <span className="admin-skeleton-line admin-skeleton-line-md" />
                      </td>
                      <td>
                        <span className="admin-skeleton-line admin-skeleton-line-sm" />
                      </td>
                      <td>
                        <span className="admin-skeleton-line admin-skeleton-line-lg" />
                      </td>
                      <td>
                        <span className="admin-skeleton-line admin-skeleton-line-lg" />
                      </td>
                      <td>
                        <span className="admin-skeleton-line admin-skeleton-line-sm" />
                      </td>
                      <td>
                        <span className="admin-skeleton-line admin-skeleton-line-md" />
                      </td>
                      <td>
                        <span className="admin-skeleton-line admin-skeleton-line-btn" />
                      </td>
                    </tr>
                  ))
                ) : visibleOrders.length === 0 ? (
                  <tr className="admin-accounts-empty-table-row">
                    <td colSpan={16} className="admin-accounts-empty-table-cell">
                      <div className="admin-accounts-empty-table-state">
                        {accountSets.length === 0
                          ? "No linked account sets found."
                          : "No pending orders found."}
                      </div>
                    </td>
                  </tr>
                ) : processedOrders.length === 0 ? (
                  <tr className="admin-accounts-empty-table-row">
                    <td colSpan={16} className="admin-accounts-empty-table-cell">
                      <div className="admin-accounts-empty-table-state">
                        No pending orders match the current filters.
                      </div>
                    </td>
                  </tr>
                ) : (
                  paginatedOrders.map((order, index) => {
                    const isLadder = order.pendingMode === "AI Smart Ladder";
                    const isPaused = isLadder && order.ladderStepStatus === "PAUSED";
                    const ladderGroupId = isLadder ? String(order.ladderGroupId || "") : "";
                    const ladderGroupRows = ladderGroupId
                      ? ladderGroupsById.get(ladderGroupId) || []
                      : [];
                    const ladderGroupIndex = ladderGroupRows.findIndex(
                      (row) => getPendingOrderKey(row) === getPendingOrderKey(order),
                    );
                    const ladderGroupBusy = Boolean(
                      ladderGroupId && ladderReorderBusy === ladderGroupId,
                    );
                    const ladderGroupHasExecuting = ladderGroupRows.some(
                      (row) => String(row.status || "").toLowerCase() === "executing",
                    );
                    const ladderGroupHasPaused = ladderGroupRows.some(
                      (row) => String(row.ladderStepStatus || "").toUpperCase() === "PAUSED",
                    );
                    const ladderCanReorder =
                      isLadder &&
                      ladderGroupRows.length > 1 &&
                      !ladderGroupHasExecuting &&
                      !ladderGroupHasPaused;
                    const ladderReorderDisabled = !ladderCanReorder || ladderGroupBusy;
                    const orderKey = getPendingOrderKey(order);
                    const isScalpingSaving = savingScalping.has(orderKey);
                    const canToggleScalping = order.status === "Pending";
                    const ipAuditEntries = getIpAuditEntries(order);

                    return (
                      <tr key={order.orderId || order.id}>
                        <td>{rowStart + index}</td>
                        <td>
                          {ipAuditEntries.length ? (
                            <div className="admin-ip-audit">
                              {ipAuditEntries.map((entry) => (
                                <button
                                  key={`${entry.label}-${entry.value}`}
                                  type="button"
                                  className="admin-ip-audit-trigger"
                                  onClick={() => setSelectedAuditIp(entry.value)}
                                  title={`Inspect ${entry.value}`}
                                >
                                  <span className="admin-ip-audit-label">{entry.label}</span>
                                  <span className="admin-ip-audit-value">{entry.value}</span>
                                </button>
                              ))}
                            </div>
                          ) : (
                            <span className="admin-ip-audit-empty">—</span>
                          )}
                        </td>
                        <td>{order.orderId || "—"}</td>
                        <td>
                          <span
                            className={`admin-badge ${order.direction === "Buy" ? "admin-badge-danger" : "admin-badge-success"}`}
                          >
                            {order.direction}
                          </span>
                        </td>
                        <td>{order.volume}</td>
                        <td>
                          {order.broker1
                            ? `${order.broker1.terminal} (${order.broker1.brokerName})`
                            : "—"}
                        </td>
                        <td>
                          {order.broker2
                            ? `${order.broker2.terminal} (${order.broker2.brokerName})`
                            : "—"}
                        </td>
                        <td className="pending-trades-strong">
                          <PremiumPairValue
                            direction={order.direction}
                            phase="entry"
                            primaryPremium={order.targetPremium}
                            showEmptyRows={false}
                          />
                        </td>
                        <td className="pending-trades-strong">
                          {hasPremiumData ? (
                            <span className="pending-trades-current-premium">
                              <span
                                className={`admin-badge pending-trades-current-premium-badge ${premiumLive ? "admin-badge-success" : collectorStale ? "admin-badge-warning" : "admin-badge-neutral"}`}
                              >
                                {premiumLive ? "Live" : collectorStale ? "Stale" : "Waiting"}
                              </span>
                              <PremiumPairValue
                                direction={order.direction}
                                phase="exit"
                                buyPremium={buyPremium}
                                sellPremium={sellPremium}
                                className="pending-trades-current-premium-value"
                              />
                            </span>
                          ) : (
                            <span className="pending-trades-waiting">—</span>
                          )}
                        </td>
                        <td>
                          <div className="pending-trades-mode-cell">
                            <span
                              className={`admin-badge ${isLadder ? "admin-badge-info" : "admin-badge-neutral"}`}
                            >
                              {isLadder ? "AI Ladder" : "Classic"}
                            </span>
                            {order.scalpingMode && (
                              <span
                                className="admin-badge pending-trades-scalping-badge"
                                title="Scalping: 3 consecutive confirmations before execution."
                              >
                                Scalping {order.scalpingConfirmationCount ?? 0}/3
                              </span>
                            )}
                          </div>
                        </td>
                        <td>
                          <label
                            className={`pending-trades-scalping-toggle pending-trades-scalping-toggle-inline${canToggleScalping ? "" : " pending-trades-scalping-toggle-disabled"}`}
                            title={
                              canToggleScalping
                                ? "Toggle scalping mode"
                                : "Only pending orders can be updated"
                            }
                          >
                            <input
                              type="checkbox"
                              className="pending-trades-scalping-input"
                              checked={Boolean(order.scalpingMode)}
                              onChange={(e) => updatePendingScalpingMode(order, e.target.checked)}
                              disabled={!canToggleScalping || isScalpingSaving}
                              aria-label={`Toggle scalping mode for order ${order.orderId || order.id || ""}`}
                            />
                            <div
                              className={`pending-trades-scalping-switch${order.scalpingMode ? " active" : ""}${isScalpingSaving ? " pending-trades-scalping-switch-loading" : ""}`}
                            >
                              <div className="pending-trades-scalping-knob" />
                            </div>
                          </label>
                        </td>
                        <td style={{ textAlign: "center" }}>
                          {isLadder ? (
                            <div className="pending-trades-ladder-info">
                              <span title={order.ladderGroupId || ""}>
                                Group{" "}
                                <strong className="pending-trades-ladder-value">
                                  {maskLadderGroupId(order.ladderGroupId)}
                                </strong>
                              </span>
                              <span>
                                Step{" "}
                                <strong className="pending-trades-ladder-value">
                                  {order.ladderStepIndex ?? "—"}
                                </strong>
                              </span>
                              <span
                                className={
                                  order.ladderStepStatus === "ELIGIBLE"
                                    ? "pending-trades-ladder-eligible"
                                    : "pending-trades-ladder-ineligible"
                                }
                              >
                                {(order.ladderStepStatus || "Waiting").charAt(0).toUpperCase() +
                                  (order.ladderStepStatus || "Waiting").slice(1).toLowerCase()}
                              </span>
                              {isPaused && (
                                <span className="pending-trades-ladder-ineligible">
                                  Paused — prev trade not closed by TP
                                  {order.ladderPauseReason ? ` (${order.ladderPauseReason})` : ""}
                                </span>
                              )}
                              {ladderGroupRows.length > 1 && (
                                <div className="pending-trades-ladder-reorder">
                                  <button
                                    type="button"
                                    className="pending-trades-ladder-reorder-btn"
                                    onClick={() => reorderLadderGroup(order, "up")}
                                    disabled={ladderReorderDisabled || ladderGroupIndex <= 0}
                                    aria-label={`Move ladder order ${order.orderId || order.id || ""} up`}
                                    title={
                                      ladderGroupHasExecuting
                                        ? LADDER_EXECUTING_MESSAGE
                                        : ladderGroupHasPaused
                                          ? LADDER_PAUSED_MESSAGE
                                          : "Move step up"
                                    }
                                  >
                                    ↑
                                  </button>
                                  <button
                                    type="button"
                                    className="pending-trades-ladder-reorder-btn"
                                    onClick={() => reorderLadderGroup(order, "down")}
                                    disabled={
                                      ladderReorderDisabled ||
                                      ladderGroupIndex < 0 ||
                                      ladderGroupIndex >= ladderGroupRows.length - 1
                                    }
                                    aria-label={`Move ladder order ${order.orderId || order.id || ""} down`}
                                    title={
                                      ladderGroupHasExecuting
                                        ? LADDER_EXECUTING_MESSAGE
                                        : ladderGroupHasPaused
                                          ? LADDER_PAUSED_MESSAGE
                                          : "Move step down"
                                    }
                                  >
                                    ↓
                                  </button>
                                </div>
                              )}
                              {ladderGroupRows.length > 1 &&
                                (ladderGroupHasExecuting || ladderGroupHasPaused) && (
                                  <span className="pending-trades-ladder-reorder-hint">
                                    {ladderGroupHasExecuting
                                      ? "Reorder waits for execution."
                                      : "Resume, skip, or reset before reorder."}
                                  </span>
                                )}
                            </div>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td>
                          {(() => {
                            const tpStatus = getPendingTpStatus(order);
                            return (
                              <div
                                className={`pending-trades-tp-value pending-trades-tp-value-${String(order.takeProfitMode || "none").toLowerCase()}`}
                              >
                                <span
                                  className={`pending-trades-tp-mode-label pending-trades-tp-mode-label-${String(order.takeProfitMode || "none").toLowerCase()}`}
                                >
                                  {getPendingTpModeLabel(order)}
                                </span>
                                <span className="pending-trades-tp-separator" aria-hidden="true" />
                                <span
                                  className={`pending-trades-tp-value-text pending-trades-tp-value-text-${String(order.takeProfitMode || "none").toLowerCase()}`}
                                >
                                  {getPendingTpDisplayValue(order)}
                                  {order.scalpingMode &&
                                    order.takeProfit &&
                                    order.takeProfitMode !== "None" && (
                                      <span className="pending-trades-tp-value-meta">
                                        {" "}
                                        ({order.scalpingConfirmationCount ?? 0}/3)
                                      </span>
                                    )}
                                </span>
                                {tpStatus.visible && (
                                  <>
                                    <span
                                      className="pending-trades-tp-separator"
                                      aria-hidden="true"
                                    />
                                    <span
                                      className={`pending-trades-tp-remaining-label pending-trades-tp-remaining-label-${String(order.takeProfitMode || "none").toLowerCase()}`}
                                    >
                                      {tpStatus.value && (
                                        <span
                                          className={
                                            tpStatus.highlight
                                              ? "pending-trades-tp-remaining-value-highlight"
                                              : ""
                                          }
                                        >
                                          {tpStatus.value}
                                        </span>
                                      )}
                                      {tpStatus.suffix}
                                    </span>
                                  </>
                                )}
                              </div>
                            );
                          })()}
                        </td>
                        <td>
                          <span
                            className={`admin-badge ${order.status === "Pending" ? "admin-badge-warning" : "admin-badge-info"}`}
                          >
                            {order.status}
                          </span>
                        </td>
                        <td>{formatDate(order.createdAt)}</td>
                        <td>
                          <div className="pending-trades-action-group">
                            {isPaused && (
                              <>
                                <button
                                  type="button"
                                  className="admin-btn admin-btn-ghost"
                                  onClick={() => handleLadderAction(order.orderId, "resume")}
                                >
                                  Resume
                                </button>
                                <button
                                  type="button"
                                  className="admin-btn admin-btn-ghost"
                                  onClick={() => handleLadderAction(order.orderId, "skip")}
                                >
                                  Skip
                                </button>
                                <button
                                  type="button"
                                  className="admin-btn admin-btn-ghost"
                                  onClick={() => handleLadderAction(order.orderId, "reset")}
                                >
                                  Reset
                                </button>
                              </>
                            )}
                            {order.status === "Pending" &&
                              (!isLadder ||
                                !["EXECUTED", "COMPLETED_TP", "SKIPPED"].includes(
                                  order.ladderStepStatus,
                                )) && (
                                <>
                                  <button
                                    type="button"
                                    className="admin-btn admin-btn-ghost"
                                    onClick={() => openEditPendingOrder(order)}
                                  >
                                    Edit
                                  </button>
                                  <button
                                    type="button"
                                    className="admin-btn admin-btn-danger"
                                    onClick={() => cancelPendingOrder(order.orderId)}
                                  >
                                    Cancel
                                  </button>
                                </>
                              )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {!loading && processedOrders.length > 0 && (
            <div className="admin-table-footer admin-users-table-footer pending-trades-table-footer">
              <span className="admin-table-summary">
                Showing {rowStart}-{rowEnd} of {processedOrders.length}
              </span>
              <div className="admin-table-pagination">
                <button
                  type="button"
                  className="admin-btn admin-btn-ghost"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft size={15} />
                  Prev
                </button>
                <span className="admin-users-pagecount pending-trades-pagecount">
                  Page {currentPage} / {pageCount}
                </span>
                <button
                  type="button"
                  className="admin-btn admin-btn-ghost"
                  onClick={() => setCurrentPage((p) => Math.min(pageCount, p + 1))}
                  disabled={currentPage >= pageCount}
                >
                  Next
                  <ChevronRight size={15} />
                </button>
              </div>
            </div>
          )}
        </section>
      </div>

      <PageModal
        open={!!editingOrder}
        onClose={closeEditPendingOrder}
        onConfirm={submitEditPendingOrder}
        title={`Edit Order #${editingOrder?.orderId}`}
        description="Update the target premium and take profit settings for this pending order."
        confirmLabel={editSaving ? "Saving…" : "Save"}
        loading={editSaving}
        icon="info"
        tone="default"
      >
        <div className="pending-edit-modal-fields">
          <div className="pending-edit-modal-topline">
            <RadioButtonGroup
              name="pending-tp-mode"
              value={editTakeProfitMode}
              onChange={(mode) => {
                setEditTakeProfitMode(mode);
                if (mode === "None") setEditTakeProfit("");
              }}
              options={[
                { value: "None", label: "None", tone: "danger" },
                { value: "Premium", label: "Premium", tone: "default" },
                { value: "Amount", label: "Amount", tone: "success" },
              ]}
            />
          </div>

          <div className="admin-field-group">
            <label className="admin-label">Expected Premium</label>
            <input
              type="number"
              step="0.01"
              className="admin-input pending-edit-modal-input"
              value={editTargetPremium}
              onChange={(e) =>
                handlePendingNumericDraftChange(setEditTargetPremium, e.target.value)
              }
              onBlur={() => {
                setEditTargetPremium(formatPendingDraftValue(editTargetPremium));
              }}
              placeholder="e.g. 28.00"
              autoFocus
            />
            <span className="pending-edit-modal-help">
              Premium at which this order will execute
            </span>
          </div>

          {editTakeProfitMode !== "None" && (
            <div className="admin-field-group">
              <label className="admin-label">
                {editTakeProfitMode === "Amount" ? "Profit Target ($)" : "Deficit Premium"}
              </label>
              <input
                type="number"
                step="0.01"
                className="admin-input pending-edit-modal-input"
                value={editTakeProfit}
                onChange={(e) => handlePendingNumericDraftChange(setEditTakeProfit, e.target.value)}
                onBlur={() => {
                  setEditTakeProfit(formatPendingDraftValue(editTakeProfit));
                }}
                placeholder={editTakeProfitMode === "Amount" ? "e.g. 50.00" : "e.g. 5.00"}
              />
              <span className="pending-edit-modal-help">
                {editTakeProfitMode === "Amount"
                  ? "Dollar amount target for auto-close"
                  : "Deficit premium target"}
              </span>
            </div>
          )}

          {editError && <p className="pending-edit-modal-error">{editError}</p>}
        </div>
      </PageModal>
      <IpAuditModal
        open={Boolean(selectedAuditIp)}
        ip={selectedAuditIp}
        onClose={() => setSelectedAuditIp("")}
      />
    </>
  );
}
